import { Pagination } from "services/search";

import {
  VehicleGroup,
  VehicleGroupEditFormValues,
  VehicleGroupRow,
  VehicleGroupVehicleRow,
} from "./types";
import {
  transformVehicleGroup,
  transformVehicleGroupOnCreate,
  transformVehicleGroupOnUpdate,
  transformVehicleGroupRow,
  transformVehicleGroupVehiclesOnCreate,
  transformVehicleGroupVehiclesRow,
} from "./transformations";
import { DataWithPagination } from "services/api/types";
import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import { transformErrorResponse } from "services/api/transformation";
import { DropdownCacheKey, invalidateDropdownCache } from "services/hooks/useDropdownCache";

/*---------------------------------------------------------------------------------------
  CALCULATION - VEHICLE GROUP - SEARCH
-----------------------------------------------------------------------------------------*/

export const getVehicleGroupsAPI = async (
  pagination: Pagination,
  name?: string | null,
  parentVehicleGroupId?: string | null
): Promise<DataWithPagination<VehicleGroupRow>> => {
  let path = `/v1/catalog/vehicles/groups?name=`;

  if (name) path += `${name}`;

  return await paginatedApiCall(path, pagination, transformVehicleGroupRow);
};

/*---------------------------------------------------------------------------------------
    CALCULATION - VEHICLE GROUP - BY ID
-----------------------------------------------------------------------------------------*/
export const getVehicleGroupAPI = async (id: string): Promise<VehicleGroup> => {
  const response = await createApiCall(
    {
      path: `/v1/catalog/vehicles/groups/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformVehicleGroup(response.data);
};

/*---------------------------------------------------------------------------------------
  CALCULATION - VEHICLE GROUP - CREATE
-----------------------------------------------------------------------------------------*/
export const createVehicleGroupAPI = async (
  formValues: VehicleGroupEditFormValues
): Promise<VehicleGroup> => {
  const response = await createApiCall(
    {
      path: "/v1/catalog/vehicles/groups/",
      method: "POST",
      body: transformVehicleGroupOnCreate(formValues),
    },
    {
      getMessage: {
        success: () => "Grupa vozila je uspješno kreirana",
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.VehicleGroups);
  return transformVehicleGroup(response.data);
};

/*---------------------------------------------------------------------------------------
    CALCULATION - VEHICLE GROUP - UPDATE
-----------------------------------------------------------------------------------------*/
export const updateVehicleGroupAPI = async (
  id: string,
  formValues: VehicleGroupEditFormValues
): Promise<VehicleGroup> => {
  const response = await createApiCall(
    {
      path: `/v1/catalog/vehicles/groups/${id}`,
      method: "PUT",
      body: transformVehicleGroupOnUpdate(formValues),
    },
    {
      getMessage: {
        success: () => "Grupa vozila je uspješno ažuriran",
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.VehicleGroups);
  return transformVehicleGroup(response.data);
};

/*---------------------------------------------------------------------------------------
    CALCULATION - VEHICLE GROUP - DELETE
-----------------------------------------------------------------------------------------*/
export const deleteVehicleGroupAPI = async (
  id: string
): Promise<VehicleGroup> => {
  const response = await createApiCall(
    {
      path: `/v1/catalog/vehicles/groups/${id}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => "Grupa vozila je uspješno obrisana",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.VehicleGroups);
  return transformVehicleGroup(response.data);
};

/*---------------------------------------------------------------------------------------
  CALCULATION - VEHICLE GROUP VEHICLES - SEARCH
-----------------------------------------------------------------------------------------*/

export const getVehicleGroupVehiclesAPI = async (
  pagination: Pagination,
  name?: string | null,
  vehicleGroupId?: string | null
): Promise<DataWithPagination<VehicleGroupVehicleRow>> => {
  let path = `/v1/catalog/vehicles/groups/${vehicleGroupId}/vehicles?name=`;

  if (name) path += `${name}`;

  return await paginatedApiCall(
    path,
    pagination,
    transformVehicleGroupVehiclesRow
  );
};

/*---------------------------------------------------------------------------------------
    CALCULATION  VEHICLE GROUP VEHICLES - CREATE
-----------------------------------------------------------------------------------------*/
export const createVehicleGroupVehiclesAPI = async (
  vehicleGroupId: string,
  vehicleIds: number[]
): Promise<VehicleGroupVehicleRow> => {
  const response = await createApiCall(
    {
      path: `/v1/catalog/vehicles/groups/${vehicleGroupId}/vehicles`,
      method: "POST",
      body: transformVehicleGroupVehiclesOnCreate(vehicleIds),
    },
    {
      getMessage: {
        success: () => "Tablica ostatka vrijednosti je uspješno kreiran",
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformVehicleGroupVehiclesRow(response.data);
};
